import { isNull, noop } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo } from 'react';
import { Options } from 'react-select';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { getUserRole } from '../../auth/login/store';
import { adminRoles, systemAdminRole, UserRole } from '../../constants/permittedRoles';
import { roleLabels } from '../../constants/roleLabels';
import { Dropdown, DropdownOption } from '../../shared/dropdown/Dropdown';
import { Icon } from '../../shared/icon/Icon';
import { Delete, User } from '../../shared/icons';
import { Tick } from '../../shared/icons/Tick';
import { ConfirmationModal } from '../../shared/modal/ConfirmationModal';
import { ModalHeader } from '../../shared/modal/ModalHeader';
import { DropzonePicture } from '../../shared/picture/DropzonePicture';
import { Scrollable } from '../../shared/scrollable/Scrollable';
import { Spinner } from '../../shared/spinner/Spinner';
import { Text } from '../../shared/text/Text';
import { TextWithIcon } from '../../shared/text/TextWithIcon';
import { Toggle } from '../../shared/toggle';
import { getAllRoles } from '../roles/store';
import { FeaturePermissions } from './FeaturePermissions';
import { checkEmailValid, checkUsernameAvailableStarted, getCheckingUsername, getEmailValid, getNewUser, getUsernameUnavailable, NewUser, updateUserValue, upsertUserStarted } from './store';
import styles from './Users.module.scss';

interface UpsertUserModalProps {
    isOpen: boolean;
    closeModal: () => void;
    clientName?: string;
    clientView?: boolean;
}

export const UpsertUserModal: React.FC<UpsertUserModalProps> = ({ isOpen, closeModal, clientName, clientView = false }) => {
    const { username, email, userId, role, roleId, featurePermissions, forenames, surname, profilePicture, silentUser } = useAppSelector(getNewUser);

    const usernameUnavailable = useAppSelector(getUsernameUnavailable);
    const checkingUsername = useAppSelector(getCheckingUsername);
    const isEmailValid = useAppSelector(getEmailValid);
    const userRole = useAppSelector(getUserRole);
    const allRoles = useAppSelector(getAllRoles);
    const isSystemAdmin = systemAdminRole.includes(userRole!);

    const userModalHeader = useMemo(() => `${userId ? 'Update' : 'Create a New'} User${clientName ? ` (${clientName})` : ''}`, [userId, clientName]);
    const userConfirmLabel = useMemo(() => userId ? 'Update' : 'Add', [userId]);

    const confirmDisabled = useMemo(() => !username.length || !email.length || usernameUnavailable || !isEmailValid || !role, [username, email, usernameUnavailable, isEmailValid, role]);

    const dispatch = useAppDispatch();
    const updateUserValueByKey = useCallback((key: keyof NewUser, value: string | number | number[] | null | boolean) => dispatch(updateUserValue(key, value)), [dispatch]);

    const updateSilentUser = useCallback((value: boolean) => {
        if (isSystemAdmin) {
            dispatch(updateUserValueByKey('silentUser', value));
        }
    }, [dispatch, isSystemAdmin, updateUserValueByKey]);

    const updateUsername = useCallback((value: string) => {
        dispatch(checkUsernameAvailableStarted());
        updateUserValueByKey('username', value);
    }, [dispatch, updateUserValueByKey]);

    const updateEmail = useCallback((value: string) => {
        updateUserValueByKey('email', value);
        dispatch(checkEmailValid(value));
    }, [updateUserValueByKey, dispatch]);

    const standardRoles = useMemo(() => [UserRole.USER, UserRole.ADMIN, UserRole.SYSTEM_ADMIN], []);

    const updateFeaturePermissions = useCallback((featurePermissions: number[]) => updateUserValueByKey('featurePermissions', featurePermissions), [updateUserValueByKey]);

    const updateRole = useCallback((value: DropdownOption | Options<DropdownOption> | null) => {
        let role: string | null = null;
        if (!isNull(value)) {
            role = (value as DropdownOption).value;
            if (!isNull(role) && !standardRoles.includes(role as UserRole)) {
                updateUserValueByKey('roleId', parseInt(role));
                updateUserValueByKey('role', UserRole.USER);
            } else {
                // If the selected role is not a custom role we set roleId back to null
                updateUserValueByKey('roleId', null);
                updateUserValueByKey('role', role);
            }
        }
    }, [standardRoles, updateUserValueByKey]);

    const upsertUser = useCallback(() => dispatch(upsertUserStarted(clientView)), [dispatch, clientView]);

    const usernameIndicator = useMemo(() => {
        if (username.length > 0) {
            if (checkingUsername) {
                return <Spinner size={11} />;
            }
            return usernameUnavailable ? <Icon fontSize={15} icon={Delete} color='red' /> : <Icon fontSize={15} icon={Tick} color='green' />;
        }
    }, [checkingUsername, username, usernameUnavailable]);

    const emailIndicator = useMemo(() => {
        if (email.length === 0) {
            return undefined;
        }
        return isEmailValid ? <Icon fontSize={15} icon={Tick} color='green' /> : <Icon fontSize={15} icon={Delete} color='red' />;
    }, [email, isEmailValid]);

    const confirmDisabledTooltip = useMemo(() => {
        let disabledArray = [];
        if (!username.length || !email.length) {
            disabledArray.push('You must include a username and email address');
        }
        if (username.length > 0 && usernameUnavailable) {
            disabledArray.push('The username you have entered is not available');
        }
        if (!isEmailValid && email.length > 0) {
            disabledArray.push('You must use a valid email address');
        }
        if (!role) {
            disabledArray.push('You must select a user role');
        }
        return disabledArray;
    }, [username, email, isEmailValid, usernameUnavailable, role]);

    const newRoles = useMemo(() => allRoles.length > 0 ? allRoles.map(({ roleId, roleName }) => ({ value: roleId!.toString(), label: roleName })) : [], [allRoles]);

    const roleOptions = useMemo(() => [
        { value: UserRole.USER, label: roleLabels[UserRole.USER] },
        { value: UserRole.ADMIN, label: roleLabels[UserRole.ADMIN] },
        ...newRoles
    ], [newRoles]);

    const roleValue = useMemo(() => isNull(roleId) ? { value: role, label: roleLabels[role] } : { value: roleId!.toString(), label: allRoles.find(role => role.roleId === roleId)!.roleName }, [roleId, role, allRoles]);

    const isAdminUser = adminRoles.includes(role);
    const isSystemAdminUser = systemAdminRole.includes(role);

    const initials = useMemo(() => `${forenames.charAt(0)}${surname.charAt(0)}`, [forenames, surname]);

    useEffect(() => {
        if (email && email.length) {
            dispatch(checkEmailValid(email));
        }
    }, [email, dispatch]);

    return (
        <ConfirmationModal
            isOpen={isOpen}
            closeModal={closeModal}
            confirmLabel={userConfirmLabel}
            confirmDisabled={confirmDisabled}
            testId='upsert-user'
            confirm={upsertUser}
            confirmDisabledTooltip={confirmDisabledTooltip}
        >
            <ModalHeader label={userModalHeader} icon={User} testId='users-modal' />
            <div className={styles.userContentWrapper}>
                <div className={styles.profilePictureWrapper}>
                    <DropzonePicture profilePicture={profilePicture} initials={initials} setProfilePicture={noop} removeProfilePicture={noop} disabled />
                    {isSystemAdmin && <div className={styles.silentSupportToggleWrapper}>
                        <div className={styles.silentSupportToggleLabel}>Silent Support User?</div>
                        <Toggle checked={!!silentUser} onChange={updateSilentUser} />
                    </div>}
                </div>
                <div className={styles.inputsWrapper} data-testid='users-modal-wrapper'>
                    <Scrollable>
                        <TextWithIcon
                            label='Username'
                            testId='upsert-user-username'
                            placeholder='Username'
                            value={username}
                            onChange={e => updateUsername(e.target.value)}
                            iconRenderer={usernameIndicator}
                            width='100%'
                        />
                        <Text
                            label='Forename(s)'
                            testId='upsert-user-forenames'
                            placeholder='Forename(s)'
                            value={forenames}
                            maxLength={128}
                            onChange={e => updateUserValueByKey('forenames', e.target.value)}
                        />
                        <Text
                            label='Surname'
                            testId='upsert-user-surname'
                            placeholder='Surname'
                            value={surname}
                            maxLength={128}
                            onChange={e => updateUserValueByKey('surname', e.target.value)}
                        />
                        <TextWithIcon
                            label='Email'
                            testId='upsert-user-email'
                            placeholder='Email'
                            value={email}
                            onChange={e => updateEmail(e.target.value)}
                            iconRenderer={emailIndicator}
                            width='100%'
                        />
                        <Dropdown
                            value={roleValue}
                            options={roleOptions}
                            testId='user-role'
                            onChange={updateRole}
                            label='Role'
                            marginBottom='10px'
                            isClearable={false}
                            menuPortalTarget={document.body}
                            disabled={!!silentUser}
                        />
                        <FeaturePermissions featurePermissionIds={featurePermissions} updateFeaturePermissions={updateFeaturePermissions} roleId={roleId} isAdmin={isAdminUser} isSystemAdmin={isSystemAdminUser} />
                        <div>
                            <div></div>
                        </div>
                    </Scrollable>
                </div>
            </div>
        </ConfirmationModal>
    );
};
